import type {
  EmailDocumentSectionResponseDto,
  WgComposeBriefDtoSectionCategory,
  EmailDocumentSectionResponseDtoCategory,
  EmailDocumentSectionsControllerListParams,
} from 'src/apis/backend/generated/models';

import { useTranslation } from 'react-i18next';
import { useRef, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useBrandResolver } from '@/hooks/use-brand-resolver';
import { useOrganization } from '@/context/organization-context';
import { SectionLibraryCard } from '@/components/email-builder/section-library/SectionLibraryCard';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Add as AddIcon,
  Star as StarIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  StarBorder as StarBorderIcon,
  SupportAgent as SupportAgentIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';

import { paths } from 'src/routes/paths';

import { useRouter } from 'src/hooks/router/use-router';
import { useSelfServeAccess } from 'src/hooks/useSelfServeAccess';

import { EmailDocumentSectionResponseDtoCategory as CategoryEnum } from 'src/apis/backend/generated/models';
import {
  useEmailDocumentSectionsControllerList,
  useEmailDocumentSectionsControllerRemove,
  useEmailDocumentSectionsControllerSetAsDefault,
} from 'src/apis/backend/generated/email-document-sections/email-document-sections';

import { useManagedBrand } from './use-managed-brand';
import { SectionSettingsDrawer } from './section-settings-drawer';

const CATEGORY_ORDER: EmailDocumentSectionResponseDtoCategory[] = [
  CategoryEnum.header,
  CategoryEnum.hero,
  CategoryEnum.content,
  CategoryEnum.cta,
  CategoryEnum.product,
  CategoryEnum.image,
  CategoryEnum.footer,
];

/**
 * A category read off the URL, or nothing.
 *
 * Validated against the list the filter itself offers rather than against every
 * value the API knows: a category the dropdown cannot show is a filter the
 * customer can see the effect of and not clear.
 */
export function toSectionCategory(
  value: unknown,
): EmailDocumentSectionResponseDtoCategory | undefined {
  return CATEGORY_ORDER.find((category) => category === value);
}

const CATEGORY_COLORS: Record<string, string> = {
  header: '#795548',
  hero: '#2196F3',
  content: '#9C27B0',
  cta: '#E91E63',
  product: '#4CAF50',
  image: '#00BCD4',
  footer: '#607D8B',
};

// This page is tenant-owned (the user's library). System rows are excluded
// at the API layer -- to pick from the system library users go through the
// email builder's library panel, not this page.
type SectionTier = 'organization' | 'client';

function tierOf(section: EmailDocumentSectionResponseDto): SectionTier {
  return section.clientId ? 'client' : 'organization';
}

const TIER_META: Record<SectionTier, { labelKey: string; color: string }> = {
  organization: { labelKey: 'dashboardSectionsList.tier.organization', color: '#3F51B5' },
  client: { labelKey: 'dashboardSectionsList.tier.client', color: '#009688' },
};

/**
 * What the Build menu offers, in the order it offers it.
 *
 * Footer first: it is the one every brand needs and the one most of them come
 * here to ask for, so it is where the eye lands when the menu opens. Each entry
 * goes to that category's request page rather than to one page with a picker
 * on it, so the choice made here is the only time the customer is asked.
 */
const BUILD_OPTIONS: {
  category: WgComposeBriefDtoSectionCategory;
  label: [string, string];
  hint: [string, string];
}[] = [
  {
    category: 'footer',
    label: ['dashboardSectionsList.build.footer', 'Footer'],
    hint: ['dashboardSectionsList.build.footerHint', 'The block every email ends on'],
  },
  {
    category: 'header',
    label: ['dashboardSectionsList.build.header', 'Header'],
    hint: ['dashboardSectionsList.build.headerHint', 'The band every email opens with'],
  },
];

export function DashboardSectionsListView({
  initialCategory,
}: {
  /** What the link that opened the library asked it to open on. */
  initialCategory?: EmailDocumentSectionResponseDtoCategory;
} = {}) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const queryClient = useQueryClient();
  const { activeOrganization, activeClient } = useOrganization();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Seeded from the URL and owned here afterwards. A link opens the library on
  // one kind of section; changing the dropdown from there is a filter change,
  // not a navigation, so the URL is not rewritten under the customer.
  const [filterCategory, setFilterCategory] = useState<
    EmailDocumentSectionResponseDtoCategory | null
  >(initialCategory ?? null);
  const [filterTier, setFilterTier] = useState<SectionTier | null>(null);
  const [buildAnchor, setBuildAnchor] = useState<HTMLElement | null>(null);
  const [settingsSectionId, setSettingsSectionId] = useState<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  };

  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';

  // A brand paying for the service can have its footer or header built rather
  // than build it. Same gate as the fifth card on the new-section picker, so the
  // offer is made to the same people wherever they meet it.
  const managed = useManagedBrand(orgId, clientId);
  // Card previews resolve $brand.* tags (logo, name, colors) against this
  // client, the same way the section's own page does.
  const { brand } = useBrandResolver(orgId, clientId);
  // The library is wider than the builder. Every brand here keeps, edits and
  // defaults its sections, but only Base makes new ones itself; the rest have
  // theirs made by us, so their way to a new section is the request above.
  const { enabled: selfServe } = useSelfServeAccess();

  const queryParams = useMemo<EmailDocumentSectionsControllerListParams>(() => {
    // excludeSystem is the defining behavior of this page: it's the user's
    // own library, not the global catalog.
    const params: EmailDocumentSectionsControllerListParams = { excludeSystem: 'true' };
    if (debouncedSearch) params.search = debouncedSearch;
    if (filterCategory) {
      params.category =
        filterCategory as unknown as EmailDocumentSectionsControllerListParams['category'];
    }
    return params;
  }, [debouncedSearch, filterCategory]);

  const { data, isLoading, error } = useEmailDocumentSectionsControllerList(
    orgId,
    clientId,
    queryParams,
    { query: { enabled: Boolean(orgId && clientId) } },
  );

  const invalidateSections = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.includes('email-document-sections');
      },
    });
  };

  const deleteMutation = useEmailDocumentSectionsControllerRemove({
    mutation: { onSuccess: invalidateSections },
  });

  const setAsDefaultMutation = useEmailDocumentSectionsControllerSetAsDefault({
    mutation: { onSuccess: invalidateSections },
  });

  const sections = data?.sections ?? [];
  const visibleSections = filterTier
    ? sections.filter((s) => tierOf(s) === filterTier)
    : sections;
  const hasActiveFilters = !!debouncedSearch || !!filterCategory || !!filterTier;

  const groupedSections = useMemo(() => {
    const groups = new Map<string, EmailDocumentSectionResponseDto[]>();
    for (const section of visibleSections) {
      const cat = section.category;
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(section);
    }
    for (const items of groups.values()) {
      items.sort((a, b) => a.name.localeCompare(b.name));
    }
    const orderIndex = (cat: string) => {
      const idx = CATEGORY_ORDER.indexOf(cat as EmailDocumentSectionResponseDtoCategory);
      return idx >= 0 ? idx : 999;
    };
    return [...groups.entries()].sort(([a], [b]) => orderIndex(a) - orderIndex(b));
  }, [visibleSections]);

  const handleDelete = async (section: EmailDocumentSectionResponseDto) => {
    if (
      !(await confirm({
        message: t('dashboardSectionsList.confirm.delete', 'Delete section "{{name}}"?', {
          name: section.name,
        }),
        confirmColor: 'error',
      }))
    )
      return;
    deleteMutation.mutate({ orgId, clientId, id: section.id });
  };

  const handleSetAsDefault = async (section: EmailDocumentSectionResponseDto) => {
    if (
      !(await confirm({
        message: t(
          'dashboardSectionsList.confirm.setDefault',
          'Make "{{name}}" the default {{category}} for this client? Future templates will use this when creating new drafts.',
          { name: section.name, category: section.category },
        ),
      }))
    )
      return;
    setAsDefaultMutation.mutate({ orgId, clientId, id: section.id });
  };

  // Only header / footer rows can be defaulted — matches the service guard so
  // we don't show an action that would be rejected at the API.
  const isDefaultable = (section: EmailDocumentSectionResponseDto) =>
    section.category === CategoryEnum.header || section.category === CategoryEnum.footer;

  // Only a brand that builds for itself is told to create one. A managed brand
  // is pointed at the request, and a brand with neither is told the plain fact.
  const emptyMessage = hasActiveFilters
    ? t('dashboardSectionsList.empty.filtered', 'No sections match the current filters.')
    : selfServe
      ? t(
          'dashboardSectionsList.empty.blank',
          'No sections yet. Create one to get started, or pick from the system library in the email builder.',
        )
      : managed
        ? t(
            'dashboardSectionsList.empty.managed',
            'No sections yet. Ask us to build a footer or header and it lands here.',
          )
        : t('dashboardSectionsList.empty.none', 'No sections yet.');

  const errorMessage = error
    ? error instanceof Error
      ? error.message
      : t('dashboardSectionsList.error.loadFailed', 'Failed to load sections')
    : deleteMutation.error
      ? t('dashboardSectionsList.error.deleteFailed', 'Failed to delete section')
      : null;

  if (!activeOrganization || !activeClient) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert severity="info">
          {t(
            'dashboardSectionsList.noContext',
            'Select an organization and client to view sections.',
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 2, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ fontSize: { xs: 24, sm: 'inherit' }, lineHeight: 1.2, mb: 0.25 }}
          >
            {t('dashboardSectionsList.title', 'Sections')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selfServe
              ? t(
                  'dashboardSectionsList.subtitle',
                  "Your own reusable email building blocks for this client and organization. The shared system library lives in the email builder's library panel.",
                )
              : t(
                  'dashboardSectionsList.subtitleProduction',
                  'Your reusable email building blocks for this client and organization.',
                )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {managed && (
            <>
              <Button
                // The page's one action for a brand we build for, and the quieter
                // second one beside New Section for a Base brand that also has us.
                variant={selfServe ? 'outlined' : 'contained'}
                startIcon={<SupportAgentIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={(event) => setBuildAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={buildAnchor ? 'true' : undefined}
                size="small"
              >
                {t('dashboardSectionsList.build.label', 'Build')}
              </Button>
              <Menu
                anchorEl={buildAnchor}
                open={!!buildAnchor}
                onClose={() => setBuildAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {BUILD_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.category}
                    onClick={() => {
                      setBuildAnchor(null);
                      router.push(
                        paths.dashboard.tenant(orgId, clientId).whiteGloveSection(option.category)
                      );
                    }}
                  >
                    <ListItemText
                      primary={t(option.label[0], option.label[1])}
                      secondary={t(option.hint[0], option.hint[1])}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {selfServe && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => router.push(paths.dashboard.sectionNew)}
              size="small"
            >
              {t('dashboardSectionsList.newSection', 'New Section')}
            </Button>
          )}
        </Box>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder={t('dashboardSectionsList.searchPlaceholder', 'Search...')}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 220 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleSearchChange('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <Autocomplete
          size="small"
          options={CATEGORY_ORDER}
          getOptionLabel={(opt) => opt}
          value={filterCategory}
          onChange={(_, opt) => setFilterCategory(opt)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('dashboardSectionsList.categoryPlaceholder', 'Category')}
            />
          )}
          sx={{ minWidth: 180 }}
        />

        <Autocomplete<SectionTier>
          size="small"
          options={['organization', 'client']}
          getOptionLabel={(opt) => t(TIER_META[opt].labelKey, opt)}
          value={filterTier}
          onChange={(_, opt) => setFilterTier(opt)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('dashboardSectionsList.scopePlaceholder', 'Scope')}
            />
          )}
          sx={{ minWidth: 160 }}
        />

        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {t('dashboardSectionsList.sectionCount', '{{count}} section', {
            count: visibleSections.length,
          })}
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} />
        </Box>
      ) : visibleSections.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {emptyMessage}
        </Alert>
      ) : (
        <Box display="flex" flexDirection="column" gap={4}>
          {groupedSections.map(([category, items]) => (
            <Box key={category}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <Chip
                  label={category}
                  sx={{
                    bgcolor: CATEGORY_COLORS[category] ?? '#757575',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    height: 28,
                    textTransform: 'capitalize',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('dashboardSectionsList.sectionCount', '{{count}} section', {
                    count: items.length,
                  })}
                </Typography>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1.5}>
                {items.map((section) => {
                  const meta = TIER_META[tierOf(section)];
                  const defaultable = isDefaultable(section);
                  return (
                    <SectionLibraryCard
                      key={section.id}
                      section={section}
                      brand={brand}
                      dim={!section.isActive}
                      onClick={() => router.push(paths.dashboard.sectionEdit(section.id))}
                      actions={
                        <>
                          {section.isDefault && (
                            <Chip
                              icon={<StarIcon sx={{ fontSize: 12 }} />}
                              label={t('dashboardSectionsList.chip.default', 'Default')}
                              size="small"
                              sx={{
                                bgcolor: '#FFC107',
                                color: '#212121',
                                height: 20,
                                fontSize: 10,
                                fontWeight: 700,
                                '& .MuiChip-icon': { color: '#212121', ml: 0.5 },
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                          <Chip
                            label={t(TIER_META[tierOf(section)].labelKey, tierOf(section))}
                            size="small"
                            sx={{
                              bgcolor: meta.color,
                              color: '#fff',
                              height: 20,
                              fontSize: 10,
                              fontWeight: 700,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          {defaultable && !section.isDefault && (
                            <Tooltip
                              title={t(
                                'dashboardSectionsList.tooltip.makeDefault',
                                'Make default {{category}}',
                                { category: section.category },
                              )}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetAsDefault(section);
                                  }}
                                  disabled={setAsDefaultMutation.isPending}
                                  sx={{
                                    bgcolor: 'background.paper',
                                    boxShadow: 1,
                                    '&:hover': { bgcolor: 'background.paper' },
                                  }}
                                >
                                  <StarBorderIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={t('dashboardSectionsList.tooltip.editDetails', 'Rename / edit details')}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSettingsSectionId(section.id);
                              }}
                              sx={{
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '&:hover': { bgcolor: 'background.paper' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!section.isDefault && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(section);
                              }}
                              sx={{
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '&:hover': { bgcolor: 'background.paper' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {settingsSectionId && (
        <SectionSettingsDrawer
          open
          onClose={() => setSettingsSectionId(null)}
          orgId={orgId}
          clientId={clientId}
          sectionId={settingsSectionId}
        />
      )}

      {ConfirmDialog}
    </Container>
  );
}
